import { useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import classNames from 'classnames/bind'
import { useTranslation } from 'next-i18next'

// Components
import FeaturedCategoryCard, { IFeaturedCategory } from './FeaturedCategoryCard'
import Image from '@/components/common/Image'
import Navigation from '@/components/shared/Navigation'

// Libs
import useMedia from '@/hooks/useMedia'
import useFeaturedCategoryList from '@/hooks/useFeaturedCategoryList'

// Types
import { FeaturedCategoryResponse, FeaturedCategoryStatus } from '@/api/fetchFeaturedCategoryList'

// Styles
import styles from './style.module.scss'

const cx = classNames.bind(styles)

const formatList = (list: FeaturedCategoryResponse | undefined): IFeaturedCategory[] => {
  if (!list) return []

  return list.map((item) => ({
    imgUrl: item.imageUrl,
    collection: item.collection,
    title: item.title,
    highlight: item.highlightText,
    link: item.linkUrl,
  }))
}

/**
 * 首頁「優惠專區」（Figma 首頁V2 Frame 101594）。
 *
 *   - ≥710：紫色橫條，左邊標題、右邊橫向卡片，可左右切換
 *   - <710：標題在上、直式小卡在下，用手滑
 */
const FeaturedCategoryList = () => {
  const { t } = useTranslation()
  const { isMobile } = useMedia()
  const { data: featuredCategoryList } = useFeaturedCategoryList({ status: FeaturedCategoryStatus.ACTIVE })
  const formattedList = formatList(featuredCategoryList)

  const swiperRef = useRef<SwiperType>()
  const [edge, setEdge] = useState({ isBeginning: true, isEnd: true })

  const updateEdge = (swiper: SwiperType) => setEdge({ isBeginning: swiper.isBeginning, isEnd: swiper.isEnd })

  if (!formattedList.length) return null

  return (
    <div className={cx('featured-category-list')}>
      <div className={cx('featured-category-list__bar')}>
        <div className={cx('featured-category-list__title')}>
          <span className={cx('featured-category-list__title-icon')}>
            <Image src='/images/svg/home-featured/alarm.svg' fill alt='' />
          </span>
          <h2 className={cx('featured-category-list__title-text')}>{t('Home_Featured_Category_Title')}</h2>
        </div>

        <div className={cx('featured-category-list__slider')}>
          <Swiper
            slidesPerView='auto'
            spaceBetween={10}
            slidesPerGroup={1}
            onSwiper={(swiper: SwiperType) => {
              swiperRef.current = swiper
              updateEdge(swiper)
            }}
            onSlideChange={updateEdge}
            onResize={updateEdge}
            onReachEnd={updateEdge}
            onReachBeginning={updateEdge}
          >
            {formattedList.map((item) => (
              <SwiperSlide key={item.title} className={cx('featured-category-list__slide')}>
                <FeaturedCategoryCard {...item} />
              </SwiperSlide>
            ))}
          </Swiper>

          {!isMobile && !edge.isBeginning && (
            <Navigation className={cx('featured-category-list__arrow', 'left')} direction='left' onClick={() => swiperRef.current?.slidePrev()} />
          )}
          {!isMobile && !edge.isEnd && (
            <Navigation className={cx('featured-category-list__arrow', 'right')} direction='right' onClick={() => swiperRef.current?.slideNext()} />
          )}
        </div>
      </div>
    </div>
  )
}

export default FeaturedCategoryList
